import type { CursorConfig } from "./types"

export const themes: Record<"minimal" | "neon" | "glassmorphism" | "brutalist", CursorConfig> = {

  // ── Minimal ──────────────────────────────────────────────────────────────────
  // Hairline borders, tiny dot, near-invisible — designed to disappear.
  minimal: {
    default: {
      width: 6, height: 6,
      shape: "circle",
      color: "rgba(255,255,255,0.9)",
      transition: { stiffness: 1100, damping: 95, mass: 0.6 },
    },
    link: {
      width: 36, height: 36,
      shape: "circle",
      color: "transparent",
      borderColor: "rgba(255,255,255,0.45)",
      borderWidth: 1,
      transition: { stiffness: 440, damping: 40, mass: 0.9 },
    },
    button: {
      width: 46, height: 46,
      shape: "circle",
      color: "transparent",
      borderColor: "rgba(255,255,255,0.35)",
      borderWidth: 1,
      label: "click",
      fontSize: "9px",
      labelColor: "rgba(255,255,255,0.55)",
      transition: { stiffness: 440, damping: 40, mass: 0.9 },
    },
    text: {
      width: 1.5, height: 24,
      shape: "square", borderRadius: 1,
      color: "rgba(255,255,255,0.75)",
      transition: { stiffness: 1400, damping: 110, mass: 0.5 },
    },
    view: {
      width: 68, height: 68,
      shape: "circle",
      color: "transparent",
      borderColor: "rgba(255,255,255,0.25)",
      borderWidth: 1,
      label: "view",
      fontSize: "10px",
      labelColor: "rgba(255,255,255,0.5)",
      transition: { stiffness: 200, damping: 24, mass: 1.6 },
    },
    drag: {
      width: 56, height: 56,
      shape: "circle",
      color: "transparent",
      borderColor: "rgba(255,255,255,0.35)",
      borderWidth: 1,
      label: "drag",
      fontSize: "9px",
      labelColor: "rgba(255,255,255,0.5)",
      transition: { stiffness: 280, damping: 30, mass: 1.2 },
    },
    close: {
      width: 36, height: 36,
      shape: "circle",
      color: "transparent",
      borderColor: "rgba(255,255,255,0.4)",
      borderWidth: 1,
      label: "✕",
      fontSize: "12px",
      labelColor: "rgba(255,255,255,0.6)",
      transition: { stiffness: 700, damping: 55, mass: 0.8 },
    },
    spot: {
      width: 110, height: 110,
      shape: "circle",
      color: "rgba(255,255,255,0.03)",
      borderColor: "rgba(255,255,255,0.08)",
      borderWidth: 1,
      transition: { stiffness: 100, damping: 16, mass: 2.5 },
    },
  },

  // ── Neon ─────────────────────────────────────────────────────────────────────
  // Bright emissive colours on a dark background. Cyan default, magenta on hover.
  neon: {
    default: {
      width: 10, height: 10,
      shape: "circle",
      color: "#00ffff",
      opacity: 0.9,
      transition: { stiffness: 900, damping: 80, mass: 0.7 },
    },
    link: {
      width: 48, height: 48,
      shape: "circle",
      color: "rgba(0,255,255,0.06)",
      borderColor: "#00ffff",
      borderWidth: 1.5,
      transition: { stiffness: 360, damping: 36, mass: 1 },
    },
    button: {
      width: 56, height: 56,
      shape: "circle",
      color: "rgba(255,0,255,0.08)",
      borderColor: "#ff00ff",
      borderWidth: 1.5,
      label: "click",
      fontSize: "10px",
      labelColor: "#ff00ff",
      transition: { stiffness: 380, damping: 38, mass: 1 },
    },
    text: {
      width: 2, height: 28,
      shape: "square",
      color: "#00ffff",
      transition: { stiffness: 1400, damping: 110, mass: 0.5 },
    },
    view: {
      width: 80, height: 80,
      shape: "circle",
      color: "rgba(0,255,255,0.04)",
      borderColor: "rgba(0,255,255,0.6)",
      borderWidth: 1.5,
      label: "view",
      fontSize: "11px",
      labelColor: "#00ffff",
      transition: { stiffness: 200, damping: 24, mass: 1.8 },
    },
    drag: {
      width: 58, height: 58,
      shape: "circle",
      color: "rgba(0,255,128,0.08)",
      borderColor: "#00ff80",
      borderWidth: 1.5,
      label: "drag",
      fontSize: "10px",
      labelColor: "#00ff80",
      transition: { stiffness: 280, damping: 30, mass: 1.2 },
    },
    close: {
      width: 48, height: 48,
      shape: "circle",
      color: "rgba(255,0,80,0.12)",
      borderColor: "#ff0050",
      borderWidth: 1.5,
      label: "✕",
      fontSize: "14px",
      labelColor: "#ff0050",
      transition: { stiffness: 650, damping: 52, mass: 0.8 },
    },
    invert: {
      width: 56, height: 56,
      shape: "circle",
      color: "rgba(255,0,255,1)",
      mixBlendMode: "difference",
      transition: { stiffness: 520, damping: 46, mass: 0.8 },
    },
    spot: {
      width: 130, height: 130,
      shape: "circle",
      color: "rgba(0,255,255,0.04)",
      borderColor: "rgba(0,255,255,0.1)",
      borderWidth: 1,
      transition: { stiffness: 100, damping: 14, mass: 2.8 },
    },
    square: {
      width: 38, height: 38,
      shape: "square", borderRadius: 4,
      color: "rgba(255,255,0,0.15)",
      borderColor: "#ffff00",
      borderWidth: 1.5,
      transition: { stiffness: 520, damping: 46, mass: 0.8 },
    },
  },

  // ── Glassmorphism ────────────────────────────────────────────────────────────
  // Translucent frosted look: soft borders, muted fill, slow spring.
  glassmorphism: {
    default: {
      width: 14, height: 14,
      shape: "circle",
      color: "rgba(255,255,255,0.55)",
      borderColor: "rgba(255,255,255,0.35)",
      borderWidth: 1,
      transition: { stiffness: 780, damping: 72, mass: 0.85 },
    },
    link: {
      width: 52, height: 52,
      shape: "circle",
      color: "rgba(255,255,255,0.08)",
      borderColor: "rgba(255,255,255,0.38)",
      borderWidth: 1,
      transition: { stiffness: 320, damping: 32, mass: 1.3 },
    },
    button: {
      width: 64, height: 64,
      shape: "circle",
      color: "rgba(255,255,255,0.1)",
      borderColor: "rgba(255,255,255,0.42)",
      borderWidth: 1,
      label: "click",
      fontSize: "10px",
      labelColor: "rgba(255,255,255,0.65)",
      transition: { stiffness: 340, damping: 34, mass: 1.3 },
    },
    text: {
      width: 2, height: 28,
      shape: "square",
      color: "rgba(255,255,255,0.65)",
      transition: { stiffness: 1400, damping: 110, mass: 0.5 },
    },
    view: {
      width: 82, height: 82,
      shape: "circle",
      color: "rgba(255,255,255,0.06)",
      borderColor: "rgba(255,255,255,0.28)",
      borderWidth: 1,
      label: "view",
      fontSize: "11px",
      labelColor: "rgba(255,255,255,0.58)",
      transition: { stiffness: 190, damping: 22, mass: 1.9 },
    },
    drag: {
      width: 62, height: 62,
      shape: "circle",
      color: "rgba(255,255,255,0.08)",
      borderColor: "rgba(255,255,255,0.32)",
      borderWidth: 1,
      label: "drag",
      fontSize: "10px",
      labelColor: "rgba(255,255,255,0.6)",
      transition: { stiffness: 270, damping: 28, mass: 1.3 },
    },
    close: {
      width: 46, height: 46,
      shape: "circle",
      color: "rgba(255,120,120,0.1)",
      borderColor: "rgba(255,120,120,0.45)",
      borderWidth: 1,
      label: "✕",
      fontSize: "14px",
      labelColor: "rgba(255,120,120,0.85)",
      transition: { stiffness: 650, damping: 52, mass: 0.8 },
    },
    invert: {
      width: 58, height: 58,
      shape: "circle",
      color: "rgba(255,255,255,1)",
      mixBlendMode: "difference",
      transition: { stiffness: 500, damping: 44, mass: 0.9 },
    },
    spot: {
      width: 130, height: 130,
      shape: "circle",
      color: "rgba(255,255,255,0.04)",
      borderColor: "rgba(255,255,255,0.1)",
      borderWidth: 1,
      transition: { stiffness: 110, damping: 16, mass: 2.6 },
    },
    square: {
      width: 40, height: 40,
      shape: "square", borderRadius: 8,
      color: "rgba(255,255,255,0.1)",
      borderColor: "rgba(255,255,255,0.35)",
      borderWidth: 1,
      transition: { stiffness: 500, damping: 44, mass: 0.9 },
    },
  },

  // ── Brutalist ────────────────────────────────────────────────────────────────
  // Hard edges, zero rounding, high contrast. The cursor as a design statement.
  brutalist: {
    default: {
      width: 8, height: 8,
      shape: "square",
      color: "#ffffff",
      transition: { stiffness: 2200, damping: 160, mass: 0.45 },
    },
    link: {
      width: 40, height: 40,
      shape: "square",
      color: "#ffffff",
      mixBlendMode: "difference",
      transition: { stiffness: 1400, damping: 100, mass: 0.6 },
    },
    button: {
      width: 72, height: 10,
      shape: "square",
      color: "#ffffff",
      label: "CLICK",
      fontSize: "8px",
      fontWeight: "900",
      labelColor: "#000000",
      transition: { stiffness: 1400, damping: 100, mass: 0.6 },
    },
    text: {
      width: 2, height: 24,
      shape: "square",
      color: "#ffffff",
      transition: { stiffness: 2200, damping: 160, mass: 0.4 },
    },
    view: {
      width: 64, height: 64,
      shape: "square",
      color: "transparent",
      borderColor: "#ffffff",
      borderWidth: 2,
      label: "VIEW",
      fontSize: "9px",
      fontWeight: "800",
      labelColor: "#ffffff",
      transition: { stiffness: 1000, damping: 75, mass: 0.8 },
    },
    drag: {
      width: 12, height: 52,
      shape: "square",
      color: "#00ff00",
      transition: { stiffness: 1400, damping: 100, mass: 0.6 },
    },
    close: {
      width: 36, height: 36,
      shape: "square",
      color: "#ff0000",
      label: "✕",
      fontSize: "16px",
      fontWeight: "900",
      labelColor: "#ffffff",
      transition: { stiffness: 1400, damping: 100, mass: 0.6 },
    },
    spot: {
      width: 100, height: 100,
      shape: "square",
      color: "transparent",
      borderColor: "rgba(255,255,255,0.2)",
      borderWidth: 2,
      transition: { stiffness: 120, damping: 18, mass: 2.5 },
    },
    square: {
      width: 38, height: 38,
      shape: "square",
      color: "#ffff00",
      transition: { stiffness: 1400, damping: 100, mass: 0.6 },
    },
    invert: {
      width: 48, height: 48,
      shape: "square",
      color: "#ffffff",
      mixBlendMode: "difference",
      transition: { stiffness: 1400, damping: 100, mass: 0.6 },
    },
  },
}
